import { useState, useEffect, useRef } from "react";
import { B } from "../../design/tokens";
import IsabelBubble from "./IsabelBubble";

const EMAIL_DOMAINS = ["gmail.com","yahoo.com","outlook.com","hotmail.com","icloud.com","live.com","ymail.com","protonmail.com"];

export default function IsabelChatbot() {
  const [open, setOpen]         = useState(false);
  const [screen, setScreen]     = useState("form"); // "form" | "chat"
  const [lead, setLead]         = useState({ name: "", mobile: "", email: "" });
  const [leadErr, setLeadErr]   = useState("");
  const [emailSuggestions, setEmailSuggestions] = useState([]);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [leadId, setLeadId]     = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [pulse, setPulse]       = useState(true);
  const [ended, setEnded]       = useState(false);
  const [botStatus, setBotStatus] = useState({ name: "Isabel", isOnline: true });
  const bottomRef               = useRef(null);
  const inputRef                = useRef(null);
  const leadIdRef               = useRef(null);
  const messagesRef             = useRef([]);

  useEffect(() => { leadIdRef.current = leadId; }, [leadId]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // Fetch chatbot status on mount
  useEffect(() => {
    const apiUrl = process.env.REACT_APP_ISABEL_API_URL || "/api/chat";
    fetch(apiUrl)
      .then(r => r.json())
      .then(d => setBotStatus({ name: d.name || "Isabel", isOnline: d.isOnline !== false }))
      .catch(() => {}); // fail open — assume online if unreachable
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveNow = (msgs, lid) => {
    const apiUrl = process.env.REACT_APP_ISABEL_API_URL || "/api/chat";
    const payload = JSON.stringify({ leadId: lid, lead: {}, messages: msgs, saveOnly: true });
    try { navigator.sendBeacon(apiUrl, new Blob([payload], { type: "application/json" })); } catch {}
  };

  useEffect(() => {
    const handler = () => {
      if (leadIdRef.current && messagesRef.current.length > 0) {
        saveNow(messagesRef.current, leadIdRef.current);
      }
    };
    window.addEventListener("beforeunload", handler);
    document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") handler(); });
    return () => window.removeEventListener("beforeunload", handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpen = () => { setOpen(o => !o); setPulse(false); };

  useEffect(() => {
    if (open && screen === "chat" && bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages, open, loading, screen]);

  useEffect(() => {
    if (open && screen === "chat" && inputRef.current) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open, screen]);

  const validateMobile = (v) => {
    if (!v) return true;
    const digits = v.replace(/[\s-]/g, "");
    return /^(09\d{9}|\+639\d{9})$/.test(digits);
  };

  const validateEmail = (v) => {
    if (!v) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  };

  const handleEmailChange = (e) => {
    const val = e.target.value;
    setLead(l => ({ ...l, email: val }));
    const atIdx = val.indexOf("@");
    if (atIdx !== -1) {
      const afterAt = val.slice(atIdx + 1).toLowerCase();
      const prefix  = val.slice(0, atIdx);
      const matches = EMAIL_DOMAINS.filter(d => d.startsWith(afterAt) && d !== afterAt);
      setEmailSuggestions(matches.map(d => `${prefix}@${d}`));
    } else {
      setEmailSuggestions([]);
    }
  };

  const formatMobile = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 11);
    if (digits.length <= 4) return digits;
    if (digits.length <= 7) return `${digits.slice(0,4)}-${digits.slice(4)}`;
    return `${digits.slice(0,4)}-${digits.slice(4,7)}-${digits.slice(7)}`;
  };

  const startChat = async () => {
    if (!lead.name.trim())            return setLeadErr("Full name is required.");
    if (!lead.mobile.trim())          return setLeadErr("Mobile number is required.");
    if (!validateMobile(lead.mobile)) return setLeadErr("Mobile must be a valid PH number (e.g. 0917-123-4567).");
    if (!validateEmail(lead.email))   return setLeadErr("Please enter a valid email address.");
    if (!privacyAccepted)             return setLeadErr("Please accept the Data Privacy consent to continue.");
    setLeadErr("");
    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_ISABEL_API_URL || "/api/chat";
      const res  = await fetch(apiUrl, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ lead, messages: [] }) });
      const data = await res.json();
      if (data.leadId) setLeadId(data.leadId);
    } catch {}
    setLoading(false);
    setScreen("chat");
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_ISABEL_API_URL || "/api/chat";
      const res = await fetch(apiUrl, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ lead, leadId, messages: next }) });
      const data = await res.json();
      if (data.offline) {
        setBotStatus(prev => ({ ...prev, isOnline: false }));
        setMessages(prev => [...prev, { role: "assistant", content: `I've gone offline unexpectedly. Please contact us directly at inquiry@neswcorp.com or 0998-859-0597.` }]);
      } else {
        setMessages(prev => [...prev, { role: "assistant", content: data.content || data.error || "Sorry, something went wrong. Please try again." }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry, I'm unable to connect right now. Please try again later." }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  const endConversation = async () => {
    setEnded(true);
    if (leadId && messages.length > 0) {
      try {
        const apiUrl = process.env.REACT_APP_ISABEL_API_URL || "/api/chat";
        await fetch(apiUrl, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ leadId, lead: {}, messages, saveOnly: true }) });
      } catch {}
    }
  };

  const GREETING = `Hello${lead.name ? `, ${lead.name}` : ""}! I'm ${botStatus.name}, your NESW concierge.\n\nHow can I assist you today? I can help with RPT, title transfer, appraisal, brokerage, property management, or environmental planning inquiries.`;

  return (
    <>
      {/* Floating Button */}
      <div style={{ position: "fixed", bottom: 28, right: 28, zIndex: 9999 }}>
        {pulse && !open && (
          <div style={{ position: "absolute", inset: -6, borderRadius: "50%", border: `2px solid ${B.azure}`, animation: "isabel-pulse 2s ease-out infinite", pointerEvents: "none" }} />
        )}
        <button onClick={handleOpen} title={open ? `Close ${botStatus.name}` : `Chat with ${botStatus.name}`}
          style={{ width: 60, height: 60, borderRadius: "50%", border: "none",
            background: open ? `linear-gradient(135deg, ${B.navy} 0%, ${B.navyMid} 100%)` : `linear-gradient(135deg, ${B.azure} 0%, ${B.royal} 100%)`,
            boxShadow: open ? `0 4px 20px ${B.navy}88` : `0 4px 24px ${B.azure}66`,
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 0.3s cubic-bezier(.16,1,.3,1)", position: "relative",
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.08)"; }}
          onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}
        >
          {/* Online/offline dot */}
          {!open && (
            <div style={{ position: "absolute", bottom: 3, right: 3, width: 12, height: 12, borderRadius: "50%", background: botStatus.isOnline ? "#22C55E" : "#ef4444", border: "2px solid #fff", zIndex: 1 }} />
          )}
          {open ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={B.white} strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
          ) : (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={B.white} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
              <circle cx="9" cy="10" r="1" fill={B.white} stroke="none"/>
              <circle cx="12" cy="10" r="1" fill={B.white} stroke="none"/>
              <circle cx="15" cy="10" r="1" fill={B.white} stroke="none"/>
            </svg>
          )}
        </button>
        {!open && (
          <div style={{ position: "absolute", right: 70, top: "50%", transform: "translateY(-50%)", background: B.navy, color: B.white, fontSize: 12.5, fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600, padding: "6px 14px", borderRadius: 20, whiteSpace: "nowrap", boxShadow: `0 2px 12px ${B.ink}44`, opacity: pulse ? 1 : 0, transition: "opacity 0.3s", pointerEvents: "none" }}>
            Chat with {botStatus.name}
          </div>
        )}
      </div>

      {/* Chat Window */}
      {open && (
        <div style={{ position: "fixed", bottom: 102, right: 28, zIndex: 9998, width: 380, maxWidth: "calc(100vw - 40px)", background: B.white, borderRadius: 20, boxShadow: `0 12px 60px ${B.ink}55, 0 2px 12px ${B.azure}22`, display: "flex", flexDirection: "column", animation: "isabel-slide-in 0.35s cubic-bezier(.16,1,.3,1)", overflow: "hidden", border: `1px solid ${B.border}` }}>
          {/* Header */}
          <div style={{ background: `linear-gradient(135deg, ${B.navy} 0%, ${B.navyMid} 100%)`, padding: "18px 20px 16px", display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: "50%", background: `linear-gradient(135deg, ${B.azure} 0%, ${B.cobalt} 100%)`, display: "flex", alignItems: "center", justifyContent: "center", border: `2px solid ${B.azure}55`, flexShrink: 0, fontSize: 19, fontWeight: 700, color: B.white, fontFamily: "'Space Grotesk', sans-serif" }}>I</div>
            <div>
              <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 16, fontWeight: 700, color: B.white, lineHeight: 1.1, letterSpacing: 0.2 }}>{botStatus.name}</div>
              <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, color: botStatus.isOnline ? B.mist : "#fca5a5", letterSpacing: 0.5, display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: botStatus.isOnline ? "#22C55E" : "#ef4444", display: "inline-block", flexShrink: 0 }} />
                NESW Concierge · {botStatus.isOnline ? "Online" : "Offline"}
              </div>
            </div>
            <div style={{ marginLeft: "auto", fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, color: B.faint, letterSpacing: 2, textTransform: "uppercase", fontWeight: 700 }}>NESW</div>
          </div>

          {/* Offline State */}
          {screen === "form" && !botStatus.isOnline && (
            <div style={{ padding: "32px 20px 28px", background: B.ghost, display: "flex", flexDirection: "column", alignItems: "center", gap: 16, textAlign: "center" }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(239,68,68,0.1)", border: "2px solid rgba(239,68,68,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18.36 6.64a9 9 0 1 1-12.73 0"/><line x1="12" y1="2" x2="12" y2="12"/>
                </svg>
              </div>
              <div>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 700, color: B.navy, marginBottom: 8 }}>
                  {botStatus.name} is currently offline
                </div>
                <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, color: B.slate, lineHeight: 1.7, marginBottom: 4 }}>
                  You may reach us directly:
                </div>
              </div>
              <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 8 }}>
                <a href="mailto:inquiry@neswcorp.com" style={{ padding: "11px 16px", borderRadius: 10, background: `${B.azure}0D`, border: `1px solid ${B.azure}22`, fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, color: B.azure, textDecoration: "none", display: "flex", alignItems: "center", gap: 8 }}>
                  ✉ inquiry@neswcorp.com
                </a>
                <a href="tel:09988590597" style={{ padding: "11px 16px", borderRadius: 10, background: `${B.azure}0D`, border: `1px solid ${B.azure}22`, fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, color: B.azure, textDecoration: "none", display: "flex", alignItems: "center", gap: 8 }}>
                  📞 0998-859-0597
                </a>
              </div>
              <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, color: B.slateLight }}>
                We'll be back online soon.
              </div>
            </div>
          )}

          {/* Pre-chat Form */}
          {screen === "form" && botStatus.isOnline && (
            <div style={{ padding: "24px 20px", background: B.ghost, display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 13.5, color: B.slate, lineHeight: 1.6 }}>
                Welcome! Before we begin, please introduce yourself.
              </div>
              <div>
                <label style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 12, color: B.slate, fontWeight: 600, display: "block", marginBottom: 5 }}>Full Name <span style={{ color: B.azure }}>*</span></label>
                <input type="text" value={lead.name} onChange={e => setLead(l => ({ ...l, name: e.target.value }))} onKeyDown={e => e.key === "Enter" && startChat()} placeholder="e.g. Juan dela Cruz"
                  style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `1.5px solid ${B.border}`, fontFamily: "'Space Grotesk', sans-serif", fontSize: 13.5, color: B.slate, background: B.white, outline: "none", boxSizing: "border-box" }}
                  onFocus={e => e.target.style.borderColor = B.azure} onBlur={e => e.target.style.borderColor = B.border} />
              </div>
              <div>
                <label style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 12, color: B.slate, fontWeight: 600, display: "block", marginBottom: 5 }}>Mobile Number <span style={{ color: B.azure }}>*</span></label>
                <input type="tel" value={lead.mobile} onChange={e => setLead(l => ({ ...l, mobile: formatMobile(e.target.value) }))} placeholder="e.g. 0917-123-4567"
                  style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `1.5px solid ${B.border}`, fontFamily: "'Space Grotesk', sans-serif", fontSize: 13.5, color: B.slate, background: B.white, outline: "none", boxSizing: "border-box" }}
                  onFocus={e => e.target.style.borderColor = B.azure} onBlur={e => e.target.style.borderColor = B.border} />
              </div>
              <div style={{ position: "relative" }}>
                <label style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 12, color: B.slate, fontWeight: 600, display: "block", marginBottom: 5 }}>Email Address <span style={{ color: B.slateLight, fontWeight: 400 }}>(optional)</span></label>
                <input type="text" value={lead.email} onChange={handleEmailChange} placeholder="e.g. juan@email.com"
                  style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `1.5px solid ${B.border}`, fontFamily: "'Space Grotesk', sans-serif", fontSize: 13.5, color: B.slate, background: B.white, outline: "none", boxSizing: "border-box" }}
                  onFocus={e => { e.target.style.borderColor = B.azure; handleEmailChange(e); }}
                  onBlur={e => { e.target.style.borderColor = B.border; setTimeout(() => setEmailSuggestions([]), 150); }} />
                {emailSuggestions.length > 0 && (
                  <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: B.white, border: `1.5px solid ${B.azure}55`, borderRadius: "0 0 10px 10px", zIndex: 20, boxShadow: `0 8px 24px ${B.navy}14`, overflow: "hidden" }}>
                    {emailSuggestions.map(s => (
                      <div key={s} onMouseDown={() => { setLead(l => ({ ...l, email: s })); setEmailSuggestions([]); }}
                        style={{ padding: "10px 14px", fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, color: B.navy, cursor: "pointer", borderBottom: `1px solid ${B.pale}`, transition: "background 0.15s" }}
                        onMouseEnter={e => e.currentTarget.style.background = `${B.azure}0d`}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        {s}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 12px", borderRadius: 10, background: privacyAccepted ? `${B.azure}0D` : B.ghost, border: `1px solid ${privacyAccepted ? B.azure : B.border}`, cursor: "pointer" }} onClick={() => setPrivacyAccepted(p => !p)}>
                <div style={{ width: 18, height: 18, borderRadius: 5, border: `2px solid ${privacyAccepted ? B.azure : B.border}`, background: privacyAccepted ? B.azure : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1, transition: "all 0.15s" }}>
                  {privacyAccepted && <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                </div>
                <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 11.5, color: B.slate, lineHeight: 1.55 }}>
                  I consent to NESW Property & Planning Consultancy OPC collecting and using my personal information (name, mobile, email) solely to respond to my inquiry, in accordance with the <span style={{ color: B.azure, fontWeight: 600 }}>Philippine Data Privacy Act of 2012 (R.A. 10173)</span>.
                </div>
              </div>
              {leadErr && <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 12.5, color: "#ef4444" }}>{leadErr}</div>}
              <button onClick={startChat} disabled={loading} style={{ padding: "12px", borderRadius: 10, border: "none", background: `linear-gradient(135deg, ${B.azure} 0%, ${B.royal} 100%)`, color: B.white, fontFamily: "'Space Grotesk', sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer", boxShadow: `0 2px 12px ${B.azure}44` }}>
                {loading ? "Starting…" : "Start Chat →"}
              </button>
            </div>
          )}

          {/* Messages */}
          {screen === "chat" && (
          <>
            <div style={{ background: "#fffbeb", borderBottom: "1px solid #fde68a", padding: "7px 14px", display: "flex", alignItems: "flex-start", gap: 8 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="0.5" fill="#d97706"/>
              </svg>
              <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10.5, color: "#92400e", lineHeight: 1.5 }}>
                This conversation is saved for quality review and audit purposes. A NESW representative will review and follow up with you.
              </span>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "20px 16px 8px", maxHeight: 360, minHeight: 200, background: B.ghost, display: "flex", flexDirection: "column", gap: 12 }}>
              {messages.length === 0 && <IsabelBubble role="assistant" text={GREETING} />}
              {messages.map((m, i) => <IsabelBubble key={i} role={m.role} text={m.content} />)}
              {loading && (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ background: B.white, border: `1px solid ${B.border}`, borderRadius: "16px 16px 16px 4px", padding: "10px 16px", display: "flex", gap: 5, alignItems: "center" }}>
                    {[0, 0.18, 0.36].map((d, i) => (
                      <div key={i} style={{ width: 7, height: 7, borderRadius: "50%", background: B.azure, animation: `isabel-dot 1.2s ${d}s ease-in-out infinite` }} />
                    ))}
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>
          </>
          )}

          {screen === "chat" && (
          <>
            {ended ? (
              <div style={{ padding: "16px", background: B.white, borderTop: `1px solid ${B.border}`, textAlign: "center" }}>
                <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, color: B.slate, marginBottom: 10 }}>
                  Thank you for chatting with Isabel! A NESW representative will be in touch soon.
                </div>
                <button onClick={() => { setEnded(false); setScreen("form"); setMessages([]); setLead({ name:"",mobile:"",email:"" }); setLeadId(null); setPrivacyAccepted(false); }}
                  style={{ padding: "8px 20px", borderRadius: 9, border: `1px solid ${B.border}`, background: "transparent", color: B.slate, fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, cursor: "pointer" }}>
                  New Inquiry
                </button>
              </div>
            ) : (
            <>
              {/* End Conversation — centered divider button */}
              <div style={{ background: B.ghost, padding: "10px 16px", display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ flex: 1, height: 1, background: B.border }} />
                <button onClick={endConversation} disabled={messages.length === 0}
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 14px", borderRadius: 20, border: `1px solid ${messages.length === 0 ? B.border : "#fca5a5"}`, background: messages.length === 0 ? "transparent" : "rgba(239,68,68,0.06)", color: messages.length === 0 ? B.slateLight : "#ef4444", fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, fontWeight: 500, cursor: messages.length === 0 ? "default" : "pointer", opacity: messages.length === 0 ? 0.35 : 1, whiteSpace: "nowrap", transition: "all 0.15s" }}>
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  End Conversation
                </button>
                <div style={{ flex: 1, height: 1, background: B.border }} />
              </div>
              {/* Input */}
              <div style={{ padding: "10px 14px 14px", background: B.white, borderTop: `1px solid ${B.border}`, display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
                  <textarea ref={inputRef} value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKey} rows={1} placeholder="Type a message…"
                    style={{ flex: 1, resize: "none", border: `1.5px solid ${B.border}`, borderRadius: 12, padding: "10px 14px", fontFamily: "'Space Grotesk', sans-serif", fontSize: 13.5, color: B.slate, background: B.ghost, outline: "none", lineHeight: 1.5, maxHeight: 100, overflowY: "auto", transition: "border-color 0.2s" }}
                    onFocus={e => { e.target.style.borderColor = B.azure; }} onBlur={e => { e.target.style.borderColor = B.border; }}
                  />
                  <button onClick={sendMessage} disabled={!input.trim() || loading}
                    style={{ width: 42, height: 42, borderRadius: "50%", border: "none", background: !input.trim() || loading ? B.border : `linear-gradient(135deg, ${B.azure} 0%, ${B.royal} 100%)`, cursor: !input.trim() || loading ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.2s", boxShadow: !input.trim() || loading ? "none" : `0 2px 10px ${B.azure}44` }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={!input.trim() || loading ? B.slateLight : B.white} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z"/>
                    </svg>
                  </button>
                </div>
              </div>
            </>
            )}
            {/* Footer */}
            <div style={{ background: B.ghost, borderTop: `1px solid ${B.border}`, padding: "6px 16px 8px", textAlign: "center" }}>
              <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10.5, color: B.slateLight, letterSpacing: 0.3 }}>
                Powered by NESW · 0998-859-0597 · inquiry@neswcorp.com
              </span>
            </div>
          </>
          )}
        </div>
      )}
    </>
  );
}
